import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { startRegistration } from '@simplewebauthn/browser';
import Swal from 'sweetalert2';

const AdminSetupPasskey = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Basic check for admin auth
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    if (!token) {
      navigate('/login');
    } else if (role !== 'admin') {
      setError('You are not authorized to access this page. Please log in as an admin.');
    }
  }, [navigate]);

  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });
  };

  const registerDevice = async () => {
    try {
      setLoading(true);
      setError('');

      // 1. Get Registration Challenge
      const challengeRes = await authFetch('/api/passkey/generate-registration-options');
      if (!challengeRes.ok) {
        throw new Error('Failed to generate registration options');
      }
      const regOptions = await challengeRes.json();

      // 2. Trigger Face ID / Touch ID
      let attResp;
      try {
        attResp = await startRegistration(regOptions);
      } catch (err) {
        if (err.name === 'InvalidStateError') {
          throw new Error('This device is already registered.');
        }
        throw new Error('Biometric registration cancelled or failed.');
      }

      // 3. Send to Server to Verify and Save
      const verifyRes = await authFetch('/api/passkey/verify-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attResp)
      });

      if (!verifyRes.ok) {
        throw new Error('Failed to verify registration with server');
      }

      Swal.fire('Success!', 'Your device has been registered for biometric authentication.', 'success');

    } catch (err) {
      setError(err.message);
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
      <Card className="shadow-lg border-0" style={{ width: '100%', maxWidth: '500px', borderRadius: '15px' }}>
        <Card.Body className="p-5 text-center">
          <div className="mb-4">
            <div style={{ width: '60px', height: '60px', borderRadius: '30px', backgroundColor: '#e3f2fd', color: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <i className="fas fa-fingerprint fa-2x"></i>
            </div>
          </div>
          
          <h3 className="fw-bold mb-3">Setup Admin Passkey</h3>
          <p className="text-muted mb-4">
            Register your device's Face ID or Touch ID to securely approve high-risk admin actions like Subscription Cancellations.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}

          <Button 
            variant="primary" 
            size="lg" 
            className="w-100"
            onClick={registerDevice}
            disabled={loading || error}
            style={{ padding: '12px', fontWeight: 'bold' }}
          >
            {loading ? <Spinner size="sm" /> : 'Register This Device'}
          </Button>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminSetupPasskey;
